"use client";

import { DoneCircle, InProgressCircle, PendingCircle } from "@health/ui";
import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { buildAssessmentText } from "./assessment-text";
import type { JourneySectionProgress } from "./journey-sections";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type JourneyProfilePanelProps = {
  isOpen: boolean;
  onClose: () => void;
  sections: JourneySectionProgress[];
  synthesis: string | null;
  title?: string;
};

// ---------------------------------------------------------------------------
// Section status icon
// ---------------------------------------------------------------------------

function StatusIcon({ status }: { status: JourneySectionProgress["status"] }) {
  switch (status) {
    case "complete":
      return <DoneCircle />;
    case "in_progress":
      return <InProgressCircle />;
    default:
      return <PendingCircle />;
  }
}

// ---------------------------------------------------------------------------
// Section row
// ---------------------------------------------------------------------------

function SectionRow({ section }: { section: JourneySectionProgress }) {
  const isPending = section.status === "pending";
  const isComplete = section.status === "complete";
  const isInProgress = section.status === "in_progress";

  return (
    <div className="flex items-start gap-3.5 border-b border-white/[0.06] py-3.5 first:pt-0">
      <span className="mt-0.5 flex shrink-0">
        <StatusIcon status={section.status} />
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        {/* Label */}
        <span
          className={`text-[13px] font-normal tracking-tight ${
            isPending ? "text-white/35" : "text-accent opacity-85"
          }`}
        >
          {isPending ? "Still needed" : section.label}
        </span>

        {/* Value / summary / pending text */}
        <span
          className={`text-xl font-normal leading-snug tracking-tight ${
            isPending ? "text-white/25" : "text-white"
          }`}
        >
          {isComplete && section.summary
            ? section.summary
            : isPending
              ? section.pendingText
              : section.label}
        </span>

        {/* In-progress: field count */}
        {isInProgress && section.fieldsTotal > 0 && (
          <span className="mt-1 text-xs text-white/40">
            {section.fieldsCollected} of {section.fieldsTotal} answered
          </span>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main panel component
// ---------------------------------------------------------------------------

export function JourneyProfilePanel({
  isOpen,
  onClose,
  sections,
  synthesis,
  title = "Your sexual\nhealth journey",
}: JourneyProfilePanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // ── All sections are always visible (pending ones show "Still needed") ──
  const visibleSections = sections;

  const assessment = buildAssessmentText(sections);

  // ── Focus management ──
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
      // Small delay to let transition start before focus shift
      const raf = requestAnimationFrame(() => {
        panelRef.current?.focus();
      });
      return () => cancelAnimationFrame(raf);
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  // ── Escape key ──
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // ── Focus trap ──
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== "Tab" || !panelRef.current) return;

    const focusable = panelRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  // ── Render title with line breaks ──
  const titleParts = title.split("\n");

  return createPortal(
    <>
      {/* Overlay backdrop */}
      <div
        className={`fixed inset-0 z-[9998] transition-colors duration-500 ease-out ${
          isOpen ? "bg-black/45 pointer-events-auto" : "bg-transparent pointer-events-none"
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal={isOpen ? "true" : undefined}
        aria-hidden={!isOpen}
        aria-label={title.replaceAll("\n", " ")}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        inert={!isOpen ? true : undefined}
        className={`fixed inset-y-0 left-0 z-[9999] flex w-full flex-col overflow-y-auto overscroll-contain bg-[#1a1210] px-7 pb-12 pt-13 outline-none scrollbar-none transition-transform duration-1000 [transition-timing-function:cubic-bezier(0.19,1,0.22,1)] motion-reduce:transition-none md:max-w-[355px] md:rounded-r-2xl ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="mb-11 flex items-start justify-between gap-4">
          <h2 className="m-0 text-[32px] font-normal leading-[1.08] tracking-[-1.5px] text-white">
            {titleParts.map((part, i) => (
              <span key={i}>
                {i > 0 && <br />}
                {part}
              </span>
            ))}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close profile"
            className="mt-1 flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-white/12 text-xl leading-none text-white/70 transition-colors duration-150 hover:bg-white/20"
          >
            ×
          </button>
        </div>

        {/* Sections */}
        <div className="mb-9 flex flex-col">
          {visibleSections.map((section) => (
            <SectionRow key={section.id} section={section} />
          ))}
        </div>

        {/* Synthesis ("What This Means") */}
        {synthesis && (
          <div className="mb-6">
            <p className="mb-2 text-[13px] font-normal tracking-tight text-accent">
              What this means
            </p>
            <p className="text-base font-normal leading-relaxed tracking-tight text-white/85">
              {synthesis}
            </p>
          </div>
        )}

        {/* Assessment card */}
        <div className="mb-8 rounded-2xl border border-accent/30 px-5 py-[18px]">
          <p className="mb-2.5 text-[13px] font-normal tracking-tight text-accent">
            Assessment so far
          </p>
          <p className="text-base font-normal leading-relaxed tracking-tight text-white/85">
            {assessment}
          </p>
        </div>

        {/* Continue button */}
        <div className="mt-auto flex flex-col items-center">
          <button
            type="button"
            onClick={onClose}
            className="h-13 w-full cursor-pointer rounded-full border-none bg-accent text-base font-medium tracking-tight text-white transition-colors duration-150 hover:brightness-90"
          >
            Continue
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
